/**
 * ErrorBoundary — TC Steward
 *
 * Without one of these a single render error unmounts the entire React tree
 * and leaves a blank white page, with the cause visible only in the console.
 * Mid-race that is indistinguishable from the app being down.
 *
 * This catches the error, keeps the rest of the app usable, and offers a way
 * back rather than requiring a refresh from a page that renders nothing.
 *
 * Note: error boundaries only catch errors thrown during rendering, in
 * lifecycle methods, and in constructors below them. They do NOT catch errors
 * inside event handlers or async callbacks — those still need their own
 * try/catch.
 */
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the full trace in the console for diagnosis
    console.error('[tc-steward] render error:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24,
        background: 'radial-gradient(circle at top, #161f6b 0%, #05081f 50%)',
      }}>
        <div style={{
          maxWidth: 460, width: '100%', background: '#0b1247', borderRadius: 16,
          padding: 28, border: '1px solid rgba(255,255,255,0.08)', color: '#eaf2ff',
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>
            Something went wrong on this screen
          </h2>
          <p style={{ margin: '0 0 18px', fontSize: 13, lineHeight: 1.6, color: '#7881c0' }}>
            The rest of the app is still running. Nothing you had saved is affected &mdash;
            this only stopped the current view from drawing.
          </p>

          <pre style={{
            background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#fca5a5',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 18px',
          }}>{String(error && error.message ? error.message : error)}</pre>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={this.handleReset} style={{
              flex: 1, padding: '11px 14px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #12d0ff, #8b5cf6)', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>Try again</button>
            <button onClick={() => { window.location.href = '/dashboard'; }} style={{
              // Full navigation on purpose: the outer boundary sits above the
              // router, so react-router's navigate() isn't available there.

              flex: 1, padding: '11px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
              color: '#8892b0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>Back to dashboard</button>
          </div>
        </div>
      </div>
    );
  }
}
